import { careerIdToLayoutData } from '@cp/entities/career';
import { useModalPromise } from '@cp/shared/modal';
import { AsyncResult, Result } from '@youhelp-ru/core';
import { useCallback } from 'react';
import { CareerId } from '@common/const/catalog/career';
import { clientTypeToSuitableCatalogItemType } from '@common/utils/catalogType';
import { ClientType } from '@common/model/client/clientType';
import { CLIENT_TYPE_MODAL, ClientSelection, withMappedSelection } from 'src/entities/client';
import { CAREER_MODAL, LOCATION_MODAL, NEED_HELP_MODAL } from '../lib';
import { useClientSelectionService } from './useClientSelectionService';
import { SelectionType } from './clientSelectionService';

export type QuizQuestions = Array<keyof ClientSelection>;
export type QuizFeature = (q: QuizQuestions) => AsyncResult<ClientSelection, Error>;

export const useQuizFeature = (): QuizFeature => {
  const service = useClientSelectionService();
  const askClientType = useModalPromise(CLIENT_TYPE_MODAL);
  const askCareerId = useModalPromise(CAREER_MODAL);
  const askLocation = useModalPromise(LOCATION_MODAL);
  const askUrgency = useModalPromise(NEED_HELP_MODAL);

  return useCallback(
    async (questions, selectionOverride: ClientSelection = {}) => {
      console.debug('Start ClientSelection/quiz feature');
      for (const question of questions) {
        const currentSelection = Object.assign(service.getSelections(), withMappedSelection(selectionOverride));
        switch (question) {
          case 'clientType':
          case 'catalogType':
            const CareerIcon = careerIdToLayoutData[service.getSelections().careerId || CareerId.Nanny].iconComponent;
            const clientType = await askClientType({ CareerIcon });
            service.register({ clientType, catalogType: clientTypeToSuitableCatalogItemType(clientType) }, SelectionType.ClientAction);
            break;
          case 'careerId':
            const titleToClientType = { [ClientType.Specialist]: 'Ищу работу', [ClientType.Employer]: 'Ищу специалиста' } as const;
            const title = currentSelection.clientType ? titleToClientType[currentSelection.clientType] : null;
            const careerId = await askCareerId({ title, clientSelection: currentSelection });
            service.register({ careerId }, SelectionType.ClientAction);
            break;
          case 'cityId':
            const loc = await askLocation({ clientSelection: service.getSelections() });
            service.register(loc, SelectionType.ClientAction);
            break;
          case 'urgencyScore':
            const urgencyScore = await askUrgency({ clientSelection: service.getSelections() });
            service.register({ urgencyScore }, SelectionType.ClientAction);
            break;
          default:
            return Result.Err(new Error(`Unsupported quiz question: ${question}`));
        }
      }
      return Result.Ok(service.getSelections());
    },
    [service, askClientType, askCareerId, askLocation, askUrgency],
  );
};
